'use client';
import React, { useRef, useState } from 'react';
import { Box, Card, CardContent, Tab, Tabs } from '@mui/material';

import { useT } from '@/hooks';
import { EventResponse } from '@/types';
import { ActivityTab } from './ActivityTab';
import { CommentsTab } from './CommentsTab';
import { TopHoldersTab } from './TopHoldersTab';

interface CommentsSectionFullProps {
  event: EventResponse;
  eventId: string | number;
  commentsCount: number;
  marketId?: never;
}

interface CommentsSectionMarketIdProps {
  marketId: string;
  event?: never;
  eventId?: never;
  commentsCount?: never;
}

type CommentsSectionProps = CommentsSectionFullProps | CommentsSectionMarketIdProps;

const TabContent = ({
  activeTab,
  eventId,
  event,
  hasFullEvent,
  scrollContainerRef,
}: {
  activeTab: number;
  eventId: string | number;
  event: EventResponse | null;
  hasFullEvent: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
}) => {
  if (!hasFullEvent) {
    return <CommentsTab eventId={eventId} scrollContainerRef={scrollContainerRef} />;
  }
  switch (activeTab) {
    case 0:
      return <CommentsTab eventId={eventId} scrollContainerRef={scrollContainerRef} />;
    case 1:
      return event ? <TopHoldersTab event={event} /> : null;
    case 2:
      return event ? <ActivityTab event={event} /> : null;
    default:
      return null;
  }
};

export const CommentsSection = (props: CommentsSectionProps) => {
  const [activeTab, setActiveTab] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useT();

  const isMarketIdMode = 'marketId' in props && props.marketId;
  const eventId = isMarketIdMode ? props.marketId : props.eventId;
  const event = isMarketIdMode ? null : (props.event ?? null);
  const commentsCount = isMarketIdMode ? 0 : (props.commentsCount ?? 0);
  const hasFullEvent = !isMarketIdMode && !!event;

  const handleTabChange = (_: unknown, val: number) => setActiveTab(val);

  return (
    <Card
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={hasFullEvent ? activeTab : 0}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9375rem',
                minWidth: 'auto',
                px: 2,
              },
              '& .Mui-selected': { color: 'primary.main', fontWeight: 600 },
              '& .MuiTabs-indicator': { bgcolor: 'primary.main' },
            }}
          >
            <Tab label={`${t('market.comments')} (${commentsCount})`} />
            {hasFullEvent && <Tab label={t('market.topHolders')} />}
            {hasFullEvent && <Tab label={t('market.activity')} />}
          </Tabs>
        </Box>

        <CardContent sx={{ p: 2 }}>
          <TabContent
            activeTab={activeTab}
            eventId={eventId}
            event={event}
            hasFullEvent={hasFullEvent}
            scrollContainerRef={scrollContainerRef}
          />
        </CardContent>
      </CardContent>
    </Card>
  );
};

export default CommentsSection;
